'use client';

import { withBase } from '../lib/brand';
import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { FaSearch, FaMapMarkerAlt, FaCampground, FaCaravan, FaHiking, FaBed, FaRoute } from 'react-icons/fa';
import { CompassOutlined } from '@ant-design/icons';

const EXPLORER_BASE = withBase('');

interface SidebarExplorerProps {
  /** Current campsite's town/suburb for contextual suggestions */
  locationName?: string;
  /** Current campsite's state (e.g. NSW, VIC) */
  state?: string;
  /** Current campsite's entity type for contextual quick-links */
  entityType?: string;
  /** Current campsite's region names for contextual links */
  regions?: { name: string; id?: number }[];
}

/** Entity-type-aware quick-link sets */
function getQuickLinks(entityType?: string, locationName?: string, state?: string) {
  const loc = locationName ? encodeURIComponent(locationName) : '';
  const stateLabel = state || 'Australia';

  // Common nearby search link — set a tight 20km radius so results stay local
  const nearbyLink = locationName
    ? { label: `Near ${locationName}`, href: `${EXPLORER_BASE}?search=${loc}&radius=20`, icon: <FaMapMarkerAlt className="text-red-500" /> }
    : null;

  const baseLinks = [
    nearbyLink,
    { label: 'Free Camping', href: `${EXPLORER_BASE}?tab=free-camping${loc ? `&search=${loc}` : ''}`, icon: <FaCampground className="text-emerald-600" /> },
    { label: 'Caravan Parks', href: `${EXPLORER_BASE}?tab=caravan-parks${loc ? `&search=${loc}` : ''}`, icon: <FaCaravan className="text-[#1558b0]" /> },
    { label: 'Accommodation', href: `${EXPLORER_BASE}?tab=accommodation${loc ? `&search=${loc}` : ''}`, icon: <FaBed className="text-purple-600" /> },
    { label: 'Activities', href: `${EXPLORER_BASE}?tab=activities${loc ? `&search=${loc}` : ''}`, icon: <FaHiking className="text-amber-600" /> },
  ].filter(Boolean) as { label: string; href: string; icon: React.ReactNode }[];

  return baseLinks;
}

const SidebarExplorer: React.FC<SidebarExplorerProps> = ({
  locationName,
  state,
  entityType,
  regions,
}) => {
  const [searchValue, setSearchValue] = useState('');

  const quickLinks = useMemo(
    () => getQuickLinks(entityType, locationName, state),
    [entityType, locationName, state]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const query = searchValue.trim();
    if (query) {
      window.location.href = `${EXPLORER_BASE}?search=${encodeURIComponent(query)}`;
    } else {
      window.location.href = EXPLORER_BASE;
    }
  };

  const contextLabel = locationName
    ? `Explore near ${locationName}`
    : 'Find Your Next Campsite';

  return (
    <div className="ca-side-explorer rounded-lg overflow-hidden border border-slate-200 shadow-sm">
      {/* Header */}
      <div className="bg-slate-900 px-5 py-4">
        <div className="flex items-center gap-2 mb-0.5">
          <CompassOutlined className="text-white text-lg" />
          <h3 className="text-base font-bold text-white">Campsite Explorer</h3>
        </div>
        <p className="text-slate-400 text-xs">{contextLabel}</p>
      </div>

      {/* Search bar */}
      <div className="bg-white px-4 pt-4 pb-3">
        <form onSubmit={handleSearch} className="relative">
          <input
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder={locationName ? `Search near ${locationName}...` : 'Location or campsite name...'}
            className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 outline-none transition-all placeholder:text-gray-400"
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          <button
            type="submit"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-semibold px-3 py-1.5 rounded-md transition-colors"
          >
            Go
          </button>
        </form>
      </div>

      {/* Quick links */}
      <div className="bg-white px-4 pb-4">
        <div className="flex flex-wrap gap-1.5">
          {quickLinks.map((link) => (
            <Link
              key={link.label}
              href={link.href}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-full hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-800 transition-all"
            >
              {link.icon}
              {link.label}
            </Link>
          ))}
        </div>

        {/* Region links */}
        {regions && regions.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Explore Region</p>
            <div className="flex flex-wrap gap-1.5">
              {regions.slice(0, 3).map((region) => (
                <Link
                  key={region.name}
                  href={withBase(`/regions/${region.name.toLowerCase().replace(/&/g, 'and').replace(/ +/g, '-').replace(/--+/g, '-')}`)}
                  className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1558b0] bg-[#e3f2fd] border border-[#e3f2fd] rounded-full hover:bg-[#e3f2fd] hover:border-[#e3f2fd] transition-all"
                >
                  <FaRoute className="text-[10px]" />
                  {region.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Browse all CTA */}
        <Link
          href={EXPLORER_BASE}
          className="flex items-center justify-center gap-2 mt-3 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-semibold transition-all shadow-sm"
        >
          <CompassOutlined />
          Browse All Campsites
        </Link>
      </div>
    </div>
  );
};

export default SidebarExplorer;
